import React, { useEffect } from 'react';
import { Container, Box, Grid, Card, CardContent, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPartnerAnalytics } from '../../redux/partnerSlice';
import { fetchPartnerGyms } from '../../redux/gymSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { useNavigate } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EventIcon from '@mui/icons-material/Event';
import StarIcon from '@mui/icons-material/Star';

export const DashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { analytics, isLoading, error } = useSelector((state: RootState) => state.partner);
  const { gyms } = useSelector((state: RootState) => state.gym);

  useEffect(() => {
    // Only fetch if we have a token
    const token = localStorage.getItem('authToken');
    if (token) {
      dispatch(fetchPartnerAnalytics({}));
      dispatch(fetchPartnerGyms());
    }
  }, [dispatch]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const summaryCards = [
    {
      title: 'Total Gyms',
      value: analytics?.total_gyms || 0,
      icon: <FitnessCenterIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      color: '#e3f2fd'
    },
    {
      title: 'Total Bookings',
      value: analytics?.total_bookings || 0,
      icon: <EventIcon sx={{ fontSize: 40, color: '#4caf50' }} />,
      color: '#e8f5e9'
    },
    {
      title: 'Total Revenue',
      value: `₹${(analytics?.total_revenue || 0).toLocaleString()}`,
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      color: '#fff3e0'
    },
    {
      title: 'Average Rating',
      value: (analytics?.average_rating || 0).toFixed(1),
      icon: <StarIcon sx={{ fontSize: 40, color: '#ffc107' }} />,
      color: '#fffde7'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Welcome back! Here's your gym management overview.
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ backgroundColor: card.color, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {card.value}
                    </Typography>
                  </Box>
                  {card.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => navigate('/gyms/create')}>
            Create New Gym
          </Button>
          <Button variant="outlined" onClick={() => navigate('/slots/create')}>
            Create Slots
          </Button>
          <Button variant="outlined" onClick={() => navigate('/gyms')}>
            View All Gyms
          </Button>
        </Box>
      </Box>

      {/* Recent Gyms */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Your Gyms
          </Typography>
          {gyms.length === 0 ? (
            <Typography color="textSecondary">No gyms created yet. Create your first gym to get started!</Typography>
          ) : (
            <Box>
              {gyms.slice(0, 5).map((gym) => (
                <Box
                  key={gym.id}
                  sx={{
                    p: 2,
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:last-child': { borderBottom: 'none' }
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {gym.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {gym.city} • Rating: {Number(gym.rating || 0).toFixed(1)} ⭐
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => navigate(`/gyms/${gym.id}`)}>
                    View Details
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default DashboardPage;
